/**
 * Exxy's calls: the streaming reply (POST /v1/assistant/chat/stream, text/event-stream) with the
 * non-streaming POST /v1/assistant/chat as a fallback when the stream can't open.
 */
import { apiConfig, useAssistantChat } from '@oex/contracts';
import { parseSse } from '../../app/sse';

export { useAssistantChat };

export type ExxyTurn = { role: 'user' | 'assistant'; content: string };

/** Thrown when the stream endpoint can't be used (non-200 or not an event stream) so the caller falls back. */
export class StreamUnavailable extends Error {
  constructor(readonly status: number) {
    super(`assistant stream unavailable (${status})`);
    this.name = 'StreamUnavailable';
  }
}

/**
 * Stream Exxy's reply, invoking `onToken` with each text chunk. Resolves when the stream ends.
 * Throws `StreamUnavailable` before any token if the endpoint can't stream (the caller then uses the
 * non-streaming mutation), or an Error if the server sends a mid-stream `error` event.
 */
export async function streamExxy(
  messages: ExxyTurn[],
  onToken: (text: string) => void,
  signal?: AbortSignal,
): Promise<void> {
  const cfg = apiConfig();
  const headers: Record<string, string> = { 'content-type': 'application/json', accept: 'text/event-stream' };
  const token = cfg.getToken();
  if (token) headers.authorization = `Bearer ${token}`;
  const orgId = cfg.getOrgId();
  if (orgId) headers['x-org-id'] = orgId;
  const doFetch = cfg.fetch ?? globalThis.fetch.bind(globalThis);
  const res = await doFetch(`${cfg.baseUrl}/v1/assistant/chat/stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ messages }),
    ...(signal ? { signal } : {}),
  });
  const contentType = res.headers.get('content-type') ?? '';
  if (!res.ok || !res.body || !contentType.includes('text/event-stream')) {
    throw new StreamUnavailable(res.status);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { messages: events, rest } = parseSse(buffer);
    buffer = rest;
    for (const m of events) {
      if (m.event === 'token') onToken((m.data as { text?: string }).text ?? '');
      else if (m.event === 'error') throw new Error((m.data as { detail?: string }).detail ?? 'The assistant stream failed.');
    }
  }
}
